import type { GanttData, Dependency, Row, Task } from './types';
import { parseDate, formatDate, diffDays, addDays } from './dateUtils';
import { genId } from './idGen';

// ─── Topological sort (Kahn's algorithm) ────────────────────────────────────
// Tasks involved in cycles are appended at the end (their dates won't be moved).

const topoSort = (taskIds: string[], deps: Dependency[]): string[] => {
  const inDeg = new Map<string, number>(taskIds.map(id => [id, 0]));
  const adj   = new Map<string, string[]>(taskIds.map(id => [id, []]));

  deps.forEach(d => {
    if (!inDeg.has(d.fromTaskId) || !inDeg.has(d.toTaskId)) return;
    adj.get(d.fromTaskId)!.push(d.toTaskId);
    inDeg.set(d.toTaskId, (inDeg.get(d.toTaskId) ?? 0) + 1);
  });

  const queue = taskIds.filter(id => inDeg.get(id) === 0);
  const result: string[] = [];

  while (queue.length > 0) {
    const id = queue.shift()!;
    result.push(id);
    adj.get(id)!.forEach(next => {
      const deg = (inDeg.get(next) ?? 1) - 1;
      inDeg.set(next, deg);
      if (deg === 0) queue.push(next);
    });
  }

  // Append cycle members (their original dates are preserved)
  const inResult = new Set(result);
  taskIds.forEach(id => { if (!inResult.has(id)) result.push(id); });

  return result;
};

// ─── Earliest start (ms) implied by one dependency ──────────────────────────

const constrainedStartMs = (
  dep: Dependency,
  fromTask: Task,
  toDurationDays: number,
): number => {
  const fromStartMs = parseDate(fromTask.startDate).getTime();
  const fromEndMs   = parseDate(fromTask.endDate).getTime();
  const MS_PER_DAY  = 86_400_000;

  switch (dep.type) {
    // to.start >= from.end
    case 'FS': return fromEndMs;

    // to.start >= from.start
    case 'SS': return fromStartMs;

    // to.end >= from.end  →  to.start >= from.end - duration
    case 'FF': return fromEndMs - toDurationDays * MS_PER_DAY;

    // to.end >= from.start  →  to.start >= from.start - duration
    case 'SF': return fromStartMs - toDurationDays * MS_PER_DAY;

    default: return 0;
  }
};

// ─── Main export ─────────────────────────────────────────────────────────────

export const autoSchedule = (state: GanttData): GanttData => {
  // Mutable copy of tasks (we'll mutate start/end dates in place)
  const taskMap = new Map<string, Task>(
    state.tasks.map(t => [t.id, { ...t }])
  );

  // Build incoming-deps index
  const incomingDeps = new Map<string, Dependency[]>(
    state.tasks.map(t => [t.id, []])
  );
  state.dependencies.forEach(d => {
    incomingDeps.get(d.toTaskId)?.push(d);
  });

  // Process tasks in topological order
  const sorted = topoSort(state.tasks.map(t => t.id), state.dependencies);

  sorted.forEach(taskId => {
    const task = taskMap.get(taskId);
    if (!task) return;

    const deps = incomingDeps.get(taskId) ?? [];
    if (deps.length === 0) return; // no incoming deps → keep original dates

    const durationDays = Math.max(1, diffDays(task.startDate, task.endDate));

    // Take the latest constraint imposed by all incoming deps
    let latestConstraint = -Infinity;

    deps.forEach(dep => {
      const from = taskMap.get(dep.fromTaskId);
      if (!from) return;
      const c = constrainedStartMs(dep, from, durationDays);
      if (c > latestConstraint) latestConstraint = c;
    });

    if (latestConstraint === -Infinity) return; // no valid constraint found

    const newStartIso = formatDate(new Date(latestConstraint));
    const newEndIso   = addDays(newStartIso, durationDays);
    task.startDate    = newStartIso;
    task.endDate      = newEndIso;
  });

  // Rebuild task list in topological order, then sort by startDate for packing
  const scheduledTasks: Task[] = sorted
    .map(id => taskMap.get(id)!)
    .filter(Boolean)
    .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));

  // Greedy first-fit interval packing into rows
  const bins: { id: string; endMs: number; taskIds: string[] }[] = [];

  scheduledTasks.forEach(task => {
    const startMs = parseDate(task.startDate).getTime();
    const endMs   = parseDate(task.endDate).getTime();
    const slot    = bins.findIndex(b => startMs >= b.endMs);

    if (slot >= 0) {
      bins[slot].taskIds.push(task.id);
      bins[slot].endMs = Math.max(bins[slot].endMs, endMs);
    } else {
      bins.push({ id: genId('row'), endMs, taskIds: [task.id] });
    }
  });

  const rows: Row[] = bins.map(b => ({ id: b.id, taskIds: b.taskIds }));

  return { ...state, tasks: scheduledTasks, rows };
};
